export const BID_REQUEST_STATUS = {
  openToBids: "OPEN_TO_BIDS",
  biddingComplete: "BIDDING_COMPLETE",
  acceptedBid: "ACCEPTED_BID",
  cancelled: "CANCELLED",
} as const;

export type BidRequestStatus = (typeof BID_REQUEST_STATUS)[keyof typeof BID_REQUEST_STATUS];

export const INVITATION_RESPONSE_STATUS = {
  awaiting: "AWAITING",
  submitted: "SUBMITTED",
  withdrawn: "WITHDRAWN",
} as const;

export type InvitationResponseStatus =
  (typeof INVITATION_RESPONSE_STATUS)[keyof typeof INVITATION_RESPONSE_STATUS];

export const BID_STATUS = {
  submitted: "SUBMITTED",
  withdrawn: "WITHDRAWN",
  won: "WON",
  lost: "LOST",
} as const;

export type BidStatus = (typeof BID_STATUS)[keyof typeof BID_STATUS];

// The demand's derived fulfilment axis (Part 2). Computed by the backend from the
// child lines + delivery + payment rows and returned inside `settlement`; never a
// stored column. The two awaiting stages come before delivery can start: the
// farmer organisation must accept the demand, then assign stock to every line.
export const FULFILMENT_STATUS = {
  awaitingAcceptance: "AWAITING_ACCEPTANCE",
  awaitingStock: "AWAITING_STOCK",
  readyForDelivery: "READY_FOR_DELIVERY",
  partiallyDelivered: "PARTIALLY_DELIVERED",
  delivered: "DELIVERED",
  pendingPayment: "PENDING_PAYMENT",
  paid: "PAID",
  cancelled: "CANCELLED",
} as const;

export type FulfilmentStatus = (typeof FULFILMENT_STATUS)[keyof typeof FULFILMENT_STATUS];

export interface Currency {
  id?: number;
  code?: string;
  name?: string;
  symbol?: string;
}

export interface BidRequestItem {
  id: number;
  commodity: number;
  commodity_name: string;
  quantity: string;
  unit_of_measure: number;
  unit_of_measure_code: string;
}

export interface BidRequestInvitation {
  id: number;
  aggregator: number;
  aggregator_name: string;
  response_status: InvitationResponseStatus;
  notified_at: string | null;
  responded_at: string | null;
}

export interface BidRequest {
  id: number;
  reference: number;
  buyer_reference: number;
  school: number;
  school_name: string;
  delivery_date: string;
  delivery_location: string;
  currency: Currency | null;
  posting_start_date: string;
  posting_end_date: string;
  status: BidRequestStatus;
  items: BidRequestItem[];
  invitations: BidRequestInvitation[];
  invited_count: number;
  accepted_bid_aggregator_name: string | null;
  // Present only once the backend list serializer exposes the demand's derived
  // fulfilment (Part 2); until then the card falls back to `status`.
  fulfilment_status?: FulfilmentStatus | null;
  created_datetime: string;
  last_update_datetime: string;
}

export interface BidItem {
  id: number;
  bid_request_item: number;
  commodity_name: string;
  unit_of_measure_code: string;
  quantity: string;
  unit_price: string;
  line_total: string;
}

export interface SchoolBid {
  id: number;
  uuid: string;
  aggregator: number;
  aggregator_name: string;
  status: BidStatus;
  transport_cost: string;
  currency: Currency | null;
  submitted_at: string | null;
  updated_at: string | null;
  items: BidItem[];
  items_total: string;
  total: string;
  is_recommended: boolean;
}

// Part 2 -- delivery + payment settlement, returned inside the comparison payload
// (GET /api/bid_requests/{id}/bids/) once a bid has been accepted.

export interface GeneratedDemand {
  id: number;
  reference: number;
}

// A deposit the farmer organisation assigned to a commodity line.
export interface SettlementDeposit {
  uuid: string;
  farmer_name: string;
  // Farmland the stock comes from (optional: absent on older backends).
  farmland_name?: string | null;
  quantity: string;
  price: string;
  quality_grade_code: string | null;
}

export interface SettlementCommodity {
  child_offer_id: number;
  commodity_id: number;
  commodity_name: string;
  unit_of_measure_id: number;
  unit_of_measure_code?: string | null;
  quality_grade_code?: string | null;
  quality_grade_name?: string | null;
  agreed_quantity: string;
  agreed_price?: string;
  delivered_quantity: string;
  remaining_quantity: string;
  // Stock assignment (optional: absent on payloads from an older backend).
  child_offer_status?: string;
  linked_quantity?: string;
  stock_complete?: boolean;
  deposits?: SettlementDeposit[];
}

export interface DeliveryLineItem {
  child_offer: number;
  commodity_id: number;
  commodity_name: string;
  unit_of_measure_id: number;
  quantity: string;
}

export interface Delivery {
  uuid: string;
  demand: number;
  aggregator: number;
  delivered_at: string;
  attachment: string | null;
  comment: string;
  items: DeliveryLineItem[];
}

export interface Payment {
  uuid: string;
  demand: number;
  aggregator: number;
  amount: string;
  payment_method: number;
  currency: number;
  paid_at: string;
  comment: string;
}

export interface Settlement {
  reference: number;
  status: string;
  fulfilment_status: FulfilmentStatus;
  commodities: SettlementCommodity[];
  agreed_total: string;
  paid_total: string;
  outstanding: string;
  deliveries: Delivery[];
  payments: Payment[];
}

export interface BidComparison {
  id: number;
  reference: number;
  buyer_reference: number;
  school: number;
  school_name: string;
  delivery_date: string;
  delivery_location: string;
  currency: Currency | null;
  posting_start_date: string;
  posting_end_date: string;
  status: BidRequestStatus;
  is_blind: boolean;
  invited_count: number;
  submitted_count: number;
  recommended_bid: number | null;
  is_recommendation_followed: boolean | null;
  decided_at: string | null;
  items: BidRequestItem[];
  invitations: BidRequestInvitation[];
  bids: SchoolBid[];
  generated_demand: GeneratedDemand | null;
  settlement: Settlement | null;
}
